/**
 * The menu behind an icon.
 *
 * The canvas used to carry a row of six text buttons in its corner, which is a toolbar on a
 * desktop and a wall on a phone. One icon that opens a list is the same six actions in 44 square
 * pixels, and it is the shape the rest of the application's overflow will take as features
 * arrive — presentation, themes, sharing — rather than a row that grows until it wraps.
 *
 * Radix again, and again for the keyboard: type-ahead, arrow keys, Escape, focus returned to the
 * trigger, `aria-expanded` maintained, and dismissal on an outside press that does not also
 * activate whatever was underneath. That last one matters on a canvas — a tap that closed the
 * menu *and* created a node would be a bug that only shows up on touch.
 */
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { IconCheck } from './icons';

const CONTENT =
  'rounded-panel border-edge bg-surface text-ink z-50 min-w-52 overflow-hidden border p-1 shadow-xl ' +
  'data-[state=open]:animate-pop-in origin-(--radix-dropdown-menu-content-transform-origin)';

const ITEM =
  'flex cursor-pointer items-center gap-2.5 rounded-md px-2.5 py-2 text-sm outline-none select-none ' +
  'data-[highlighted]:bg-surface-sunken data-[disabled]:pointer-events-none data-[disabled]:opacity-40';

export interface MenuProps {
  /** The control the menu hangs off. Must accept a ref and spread its props — an `IconButton` does. */
  readonly trigger: React.ReactNode;
  readonly align?: 'start' | 'center' | 'end';
  readonly side?: 'top' | 'right' | 'bottom' | 'left';
  /**
   * Called as the menu closes, before focus goes back to the trigger.
   *
   * Preventing the default keeps focus wherever the chosen item put it. There are two things that
   * take focus away from a panel opened by a menu item, and they need separate refusals: the
   * panel's own focus trap, and this. Refusing only one of them leaves the caret on the other's
   * choice, which is how the note field ended up unfocused twice for two different reasons.
   */
  readonly onCloseAutoFocus?: (event: Event) => void;
  readonly children: React.ReactNode;
}

export function Menu({
  trigger,
  align = 'end',
  side = 'top',
  onCloseAutoFocus,
  children,
}: MenuProps): React.JSX.Element {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>{trigger}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align={align}
          side={side}
          sideOffset={8}
          {...(onCloseAutoFocus === undefined ? {} : { onCloseAutoFocus })}
          className={CONTENT}
        >
          {children}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function MenuItem({
  icon,
  hint,
  disabled,
  keepOpen = false,
  onSelect,
  children,
}: {
  icon?: React.ReactNode;
  hint?: string;
  disabled?: boolean | undefined;
  /**
   * Leaves the menu open after this item runs.
   *
   * For the handful of actions a person takes more than once in a row — zooming is the whole
   * list — where closing the menu means reopening it to press the same thing again.
   */
  keepOpen?: boolean;
  onSelect: () => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <DropdownMenu.Item
      // Spread rather than passed: under `exactOptionalPropertyTypes` an explicit `undefined` is
      // not the same as an absent prop, and Radix's `disabled` does not accept the former.
      {...(disabled === undefined ? {} : { disabled })}
      onSelect={(event) => {
        if (keepOpen) event.preventDefault();
        onSelect();
      }}
      className={`${ITEM} justify-between`}
    >
      <span className="flex items-center gap-2.5">
        {icon !== undefined && <span className="text-ink-muted">{icon}</span>}
        {children}
      </span>
      {hint !== undefined && <kbd className="text-ink-faint font-mono text-xs">{hint}</kbd>}
    </DropdownMenu.Item>
  );
}

export function MenuLabel({ children }: { children: React.ReactNode }): React.JSX.Element {
  return (
    <DropdownMenu.Label className="text-ink-faint px-2.5 pt-2 pb-1 text-[0.6875rem] font-semibold tracking-[0.08em] uppercase">
      {children}
    </DropdownMenu.Label>
  );
}

export function MenuSeparator(): React.JSX.Element {
  return <DropdownMenu.Separator className="bg-edge my-1 h-px" />;
}

export function MenuRadioGroup({
  value,
  onValueChange,
  children,
}: {
  value: string;
  onValueChange: (value: string) => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <DropdownMenu.RadioGroup value={value} onValueChange={onValueChange}>
      {children}
    </DropdownMenu.RadioGroup>
  );
}

export function MenuRadioItem({
  value,
  children,
}: {
  value: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <DropdownMenu.RadioItem value={value} className={ITEM}>
      {/* Fixed width, so the labels line up whether or not a row is the chosen one. */}
      <span className="text-accent flex w-4 shrink-0 justify-center">
        <DropdownMenu.ItemIndicator>
          <IconCheck size={16} />
        </DropdownMenu.ItemIndicator>
      </span>
      {children}
    </DropdownMenu.RadioItem>
  );
}
